/**
 * Media Kit images are restricted to the brand Cloudinary folder:
 *   `{BRAND}/mediakit`  e.g. `delfuocampo/mediakit`
 *
 * Admin uploads/picker already target that leaf; public page + merge
 * refuse any public_id / URL that is not under this folder.
 */
import { BRAND_CLOUDINARY_FOLDER } from "@/lib/brandConfig";
import type { CloudinaryImage } from "./types";

const MEDIAKIT_LEAF = "mediakit";

function resolveBrandBase(): string {
  const raw =
    (import.meta.env.VITE_CLOUDINARY_BASE_FOLDER as string | undefined)?.trim() ||
    (import.meta.env.VITE_BRAND_CLOUDINARY_FOLDER as string | undefined)?.trim() ||
    BRAND_CLOUDINARY_FOLDER ||
    "delfuocampo";
  // If env was mistakenly set to "delfuocampo/portfolio", strip to brand root
  const base = raw.split("/").filter(Boolean)[0] || "delfuocampo";
  return base;
}

/** e.g. `delfuocampo/mediakit` */
export function getMediaKitFolderPath(): string {
  return `${resolveBrandBase()}/${MEDIAKIT_LEAF}`;
}

/** Leaf only — for admin gallery `folderFilter` / upload subfolder. */
export function getMediaKitSubfolder(): string {
  return MEDIAKIT_LEAF;
}

/**
 * Extract Cloudinary public_id from a full delivery URL, or return the id as-is.
 * Strips `/image/upload/.../` transform + version segments.
 */
export function extractCloudinaryPublicId(value: string): string {
  const v = value.trim();
  if (!v) return "";
  if (!/^https?:\/\//i.test(v)) {
    return v.replace(/^\/+/, "").replace(/\.[a-z0-9]+$/i, "");
  }
  try {
    const u = new URL(v);
    const marker = "/image/upload/";
    const idx = u.pathname.indexOf(marker);
    if (idx === -1) return "";
    let rest = u.pathname.slice(idx + marker.length);
    // Drop transform segment(s) until we hit version or public id path
    // Pattern: [transforms/]v123456/public/id.ext  OR  public/id.ext
    const parts = rest.split("/").filter(Boolean);
    let start = 0;
    // Skip transformation chunks that contain `_` (f_auto, w_800, …) but not pure version
    while (start < parts.length) {
      const p = parts[start];
      if (/^v\d+$/.test(p)) {
        start += 1;
        break;
      }
      // transform tokens look like f_auto,q_auto,c_fill or c_fill,w_800
      if (p.includes(",") || /^[a-z]_[a-z0-9]+/i.test(p) || p.includes("%2C")) {
        start += 1;
        continue;
      }
      break;
    }
    const id = parts.slice(start).join("/");
    return id.replace(/\.[a-z0-9]+$/i, "");
  } catch {
    return "";
  }
}

/**
 * True if the image lives under `{brand}/mediakit` (public_id prefix or URL path).
 * Also accepts root `mediakit/` only when brand is empty (legacy) — not used here.
 */
export function isMediaKitFolderAsset(publicIdOrUrl: string): boolean {
  if (!publicIdOrUrl?.trim()) return false;
  const folder = getMediaKitFolderPath().toLowerCase();
  const id = extractCloudinaryPublicId(publicIdOrUrl).toLowerCase();
  if (!id) return false;
  return id === folder || id.startsWith(`${folder}/`);
}

/**
 * If the image is outside the brand mediakit folder, replace with `fallback`.
 */
export function constrainToMediaKitFolder(
  image: CloudinaryImage,
  fallback: CloudinaryImage,
): CloudinaryImage {
  if (isMediaKitFolderAsset(image?.publicId ?? "")) {
    // Normalize to public_id form (better srcset / transforms)
    const id = extractCloudinaryPublicId(image.publicId);
    return {
      ...image,
      publicId: id || image.publicId,
    };
  }
  return { ...fallback };
}
